(() => {
  'use strict';
  function quantityRules(variant) {
    const min = Math.max(1, Number(variant?.min) || 1);
    const step = Math.max(1, Number(variant?.step) || 1);
    const max = Number(variant?.max) > 0 ? Number(variant.max) : Infinity;
    return { min, step, max };
  }
  function validQuantity(value, variant) {
    const { min, step, max } = quantityRules(variant);
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= min && number <= max && (number - min) % step === 0;
  }
  function normalizeQuantity(value, variant) {
    const { min, step, max } = quantityRules(variant);
    const number = Number(value);
    const last = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity;
    return Math.max(min, Math.min(last, min + Math.floor((Number.isFinite(number) ? number - min : 0) / step) * step));
  }
  function quantityPricing(price, quantity) {
    const percent = quantity >= 30 ? 20 : quantity >= 10 ? 15 : quantity >= 5 ? 10 : 0;
    const original = price * quantity;
    const total = Math.round(original * (100 - percent) / 100);
    return { percent, original, total, saving: original - total };
  }
  function deliveryRange(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const day = Object.fromEntries(parts.map(part => [part.type, part.value]));
    const start = new Date(Date.UTC(Number(day.year), Number(day.month) - 1, Number(day.day) + 3));
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 2);
    const format = new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', day: 'numeric', month: 'short' });
    return start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()
      ? start.getUTCDate() + '–' + format.format(end)
      : format.format(start) + '–' + format.format(end);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { quantityRules, validQuantity, normalizeQuantity, quantityPricing, deliveryRange };
  if (typeof customElements === 'undefined' || customElements.get('pp-ganesha-gift')) return;

  class GaneshaGift extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      const config = this.querySelector('[data-gg-config]');
      if (!config) return;
      this.config = JSON.parse(config.textContent);
      this.comparison = JSON.parse(this.querySelector('[data-gg-comparison-config]')?.textContent || '{}');
      this.form = this.querySelector('[data-gg-form]');
      this.quantity = this.querySelector('[data-gg-quantity]');
      this.idInput = this.querySelector('[data-gg-id]');
      this.sticky = this.querySelector('[data-gg-sticky]');
      this.slides = [...this.querySelectorAll('[data-gg-slide]')];
      this.index = 0;
      const currency = this.config.currency || 'INR';
      const wholeMoney = new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 });
      const fractionalMoney = new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2, minimumFractionDigits: 2 });
      this.money = { format: amount => (Number.isInteger(amount) ? wholeMoney : fractionalMoney).format(amount) };
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.add(event.submitter?.dataset.ggAction || 'cart', event.submitter);
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-gg-compare-select]')) {
          const comparison = this.comparison[event.target.value];
          if (comparison) {
            this.querySelector('[data-gg-compare-name]').textContent = comparison.name;
            this.querySelectorAll('[data-gg-compare-value]').forEach(cell => { cell.textContent = comparison.values[Number(cell.dataset.ggCompareValue)]; });
          }
        }
        if (event.target.matches('[data-gg-edition]')) {
          this.quantity.value = normalizeQuantity(this.quantity.value, this.selection().variant);
          this.sync();
          this.message('');
        }
      }, options);
      this.quantity.addEventListener('input', () => this.sync(), options);
      this.addEventListener('click', event => {
        const go = event.target.closest('[data-gg-go]');
        if (go) this.go(Number(go.dataset.ggGo));
        if (event.target.closest('[data-gg-prev]')) this.go(this.index - 1);
        if (event.target.closest('[data-gg-next]')) this.go(this.index + 1);
        const minus = event.target.closest('[data-gg-minus]');
        const plus = event.target.closest('[data-gg-plus]');
        const pack = event.target.closest('[data-gg-pack]');
        if (pack && !pack.disabled && !this.pending) {
          this.quantity.value = normalizeQuantity(Number(pack.dataset.ggPack), this.selection().variant);
          this.sync();
          this.message('');
        }
        if (minus || plus) {
          const variant = this.selection().variant;
          const step = quantityRules(variant).step;
          this.quantity.value = normalizeQuantity(Number(this.quantity.value) + (plus ? step : -step), variant);
          this.sync();
        }
        const select = event.target.closest('[data-gg-select]');
        if (select) {
          const radio = this.querySelector(`[data-gg-edition][value="${select.dataset.ggSelect}"]`);
          if (!radio) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          this.querySelector('.gg-info').scrollIntoView({ block: 'start', behavior: 'auto' });
          radio.focus({ preventScroll: true });
        }
        // Keep imported store-review content on this product page.
        const reviewLink = event.target.closest('.gg-reviews a');
        if (reviewLink) event.preventDefault();
      }, options);
      this.querySelector('[data-gg-gallery]').addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      this.addEventListener('play', event => this.querySelectorAll('video').forEach(video => { if (video !== event.target) video.pause(); }), { ...options, capture: true });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pauseVideos(); else this.syncDelivery(); }, options);
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateSticky(); });
      };
      window.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      window.addEventListener('resize', this.onScroll, { ...options, passive: true });
      window.addEventListener('pageshow', () => { this.sync(); this.syncDelivery(); this.updateSticky(); }, options);
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer) {
        this.observer = new MutationObserver(() => this.updateSticky());
        this.observer.observe(drawer, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
      }
      this.sync();
      this.syncDelivery();
      this.go(0);
      this.updateSticky();
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.pauseVideos();
    }
    selection() {
      const key = this.querySelector('[data-gg-edition]:checked')?.value || 'classic';
      return { key, variant: this.config.variants[key], quantity: Number(this.quantity.value) };
    }
    sync() {
      const { variant, quantity } = this.selection();
      const rules = quantityRules(variant);
      const valid = validQuantity(quantity, variant);
      const available = !!variant?.available;
      this.quantity.min = rules.min;
      this.quantity.step = rules.step;
      if (Number.isFinite(rules.max)) this.quantity.max = rules.max;
      else this.quantity.removeAttribute('max');
      this.idInput.value = variant?.id || '';
      this.idInput.disabled = this.config.locked || !variant?.id;
      this.querySelector('[data-gg-price]').textContent = this.money.format((variant?.price || 0) / 100);
      this.querySelector('[data-gg-note]').textContent = variant?.note || '';
      this.querySelector('[data-gg-sticky-edition]').textContent = variant?.shortTitle || variant?.title || '';
      this.querySelector('[data-gg-detail-material]').textContent = variant?.material || '';
      this.querySelector('[data-gg-detail-copy]').textContent = variant?.detail || '';
      const price = quantityPricing(variant?.price || 0, valid ? quantity : rules.min);
      const { total, percent, saving, original } = price;
      this.querySelector('[data-gg-sticky-price]').textContent = this.money.format(total / 100);
      const originalLabel = this.querySelector('[data-gg-original]');
      originalLabel.hidden = !valid || !percent;
      originalLabel.textContent = this.money.format(original / 100);
      const savingLabel = this.querySelector('[data-gg-saving]');
      savingLabel.hidden = !valid || !percent;
      savingLabel.textContent = this.config.strings.saving.replace('[percent]', percent).replace('[saving]', this.money.format(saving / 100));
      this.querySelectorAll('[data-gg-pack]').forEach(button => {
        const tier = Number(button.dataset.ggPack);
        const selectedTier = quantity >= 30 ? 30 : quantity >= 10 ? 10 : quantity >= 5 ? 5 : 1;
        button.setAttribute('aria-pressed', String(valid && tier === selectedTier));
        button.disabled = !!this.pending || tier > rules.max;
      });
      const quantityLabel = this.querySelector('[data-gg-sticky-quantity]');
      quantityLabel.hidden = !valid || quantity <= 1;
      quantityLabel.textContent = this.config.strings.boxes.replace('[count]', quantity);
      this.querySelector('[data-gg-total]').textContent = ((valid ? quantity : rules.min) === 1 ? this.config.strings.totalOne : this.config.strings.total).replace('[count]', valid ? quantity : rules.min).replace('[price]', this.money.format(total / 100));
      this.querySelector('[data-gg-minus]').disabled = this.pending || quantity <= rules.min;
      this.querySelector('[data-gg-plus]').disabled = this.pending || quantity + rules.step > rules.max;
      this.quantity.disabled = !!this.pending;
      this.querySelectorAll('[data-gg-edition]').forEach(input => { input.disabled = !!this.pending; });
      this.querySelectorAll('[data-gg-select]').forEach(button => { button.disabled = !!this.pending; });
      this.querySelectorAll('[data-gg-action]').forEach(button => { button.disabled = !!(this.config.locked || this.pending || this.uncertain || !available || !valid || !variant?.id); });
      this.querySelectorAll('[data-gg-add-label]').forEach(label => { label.textContent = this.pending && this.intent === 'cart' ? this.config.strings.adding : available ? this.config.strings.add : this.config.strings.soldOut; });
      this.querySelector('[data-gg-buy-label]').textContent = this.pending && this.intent === 'checkout' ? this.config.strings.opening : available ? this.config.strings.buy : this.config.strings.soldOut;
    }
    syncDelivery() {
      this.querySelectorAll('[data-gg-delivery]').forEach(label => {
        label.textContent = this.config.strings.deliveryEstimate.replace('[range]', deliveryRange());
      });
    }
    go(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.querySelectorAll('[data-gg-go]').forEach((button, i) => button.setAttribute('aria-current', String(i === this.index)));
      this.querySelector('[data-gg-caption]').textContent = this.config.captions[this.index] || '';
    }
    updateSticky() {
      if (!this.sticky) return;
      const buy = this.querySelector('[data-gg-purchase]').getBoundingClientRect();
      const drawer = document.querySelector('m-cart-drawer');
      const drawerOpen = drawer?.classList.contains('m-cart-drawer--active') || drawer?.classList.contains('active') || drawer?.hasAttribute('open');
      this.sticky.hidden = buy.bottom > 0 || !!drawerOpen;
    }
    pauseVideos() { this.querySelectorAll('video').forEach(video => video.pause()); }
    message(text, error = false, cartLink = false) {
      const status = this.querySelector('[data-gg-status]');
      status.replaceChildren(document.createTextNode(text));
      status.hidden = !text;
      status.toggleAttribute('data-error', error);
      status.setAttribute('role', error ? 'alert' : 'status');
      if (cartLink) {
        const link = document.createElement('a');
        link.href = (window.Shopify?.routes?.root || '/') + 'cart';
        link.textContent = this.config.strings.cart;
        status.append(document.createTextNode(' '), link);
      }
    }
    async add(intent = 'cart', submitter) {
      if (this.config.locked || this.pending || this.uncertain) return;
      const selected = this.selection();
      if (!validQuantity(selected.quantity, selected.variant) || !selected.variant?.available || !Number.isSafeInteger(selected.variant.id) || selected.variant.id <= 0) return;
      const root = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('m-cart-drawer');
      if (cart?.ppCartBusy?.() || cart?.hasAttribute('data-pp-add-pending') || cart?.hasAttribute('data-pp-add-uncertain')) return;
      const ids = [...new Set((cart?.getSectionsToRender?.() || []).map(section => section.id))].slice(0, 5);
      this.pending = true;
      this.intent = intent;
      this.message('');
      this.sync();
      const trigger = submitter || this.querySelector(intent === 'checkout' ? '[data-gg-buy]' : '[data-gg-add]');
      cart?.setActiveElement?.(trigger);
      cart?.setAttribute('data-pp-add-pending', '');
      cart?.ppSyncCart?.();
      if (intent === 'cart') cart?.open?.(trigger);
      let confirmed = false;
      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: selected.variant.id, quantity: selected.quantity }], sections: ids, sections_url: location.pathname + location.search }),
        });
        const result = await response.json();
        if (!response.ok || result.status) { this.message(result.description || this.config.strings.error, true); return; }
        confirmed = true;
        this.message(this.config.strings.added);
        const first = result.items?.[0];
        const cartResult = { ...result, id: first?.id, variant_id: first?.variant_id, product_id: first?.product_id };
        // Use the existing cart/checkout integrations; do not send extra tracking events.
        try {
          const event = window.MinimogTheme?.pubSubEvents?.cartUpdate;
          if (event) window.MinimogEvents?.emit(event, { ...cartResult, source: 'main-cart-items' });
        } catch (_) {}
        if (!cart?.renderContents || !ids.every(id => typeof result.sections?.[id] === 'string')) { location.assign(root + 'cart'); return; }
        cart.renderContents(cartResult);
        cart.removeAttribute('data-pp-add-pending');
        cart.ppSyncCart?.();
        cart.open?.(trigger);
        if (intent === 'checkout') {
          await new Promise(resolve => setTimeout(resolve, 0));
          const host = cart.querySelector('.shiprocket-headless');
          const checkout = host?.querySelector('.sr-headless-checkout');
          if (checkout && !checkout.disabled && checkout.getAttribute('aria-disabled') !== 'true' && getComputedStyle(host).display !== 'none' && !cart.classList.contains('pp-cart-terms-required')) checkout.click();
          else this.message(this.config.strings.fallback, false, true);
        }
      } catch (_) {
        this.uncertain = !confirmed;
        this.message(confirmed ? this.config.strings.fallback : this.config.strings.uncertain, !confirmed, true);
        if (cart?.ppConfirmCart) await cart.ppConfirmCart();
        else cart?.onCartDrawerUpdate?.();
      } finally {
        cart?.removeAttribute('data-pp-add-pending');
        cart?.ppSyncCart?.();
        this.pending = false;
        this.sync();
      }
    }
  }
  customElements.define('pp-ganesha-gift', GaneshaGift);
})();
